import React, { useEffect, useState } from 'react';
import { Mail, Twitter, Youtube, Instagram, Facebook, Share2 } from 'lucide-react';
import SOCIAL_LINKS from '../config/social-links';

const Footer = () => {
  const partners = [
    { name: 'Tailgreeter', url: 'https://tailgreeter.com/?affiliate=40bags' },
    { name: 'StubHub', url: 'https://www.stubhub.com/?gcid=chAFF-_-geoUS-_-genTICKETS-_-dt170201-_-40bags' },
    { name: 'Ticketmaster', url: 'https://www.ticketmaster.com/affiliates/?tm_link=40bags' },
    { name: 'Expedia', url: 'https://www.expedia.com/?affcid=EXPEDIA.AFFILIATE.40BAGS' },
    { name: 'VRBO', url: 'https://www.vrbo.com/?affiliateId=40bags' },
    { name: 'Hotels.com', url: 'https://www.hotels.com/?affid=40bags' }
  ];

  const startPlanning = [
    { name: 'Booking Travel', url: '/' },
    { name: 'Tailgating', url: '/tailgate-central' },
    { name: 'Music', url: '/playlists' },
    { name: 'Merch', url: '/merch' },
    // { name: 'Blog', url: '/blog' }, // Hidden - not ready for public viewing
    { name: 'About Us', url: '/about' }
  ];

  const XIcon = (props) => (
    <svg viewBox="0 0 24 24" width={props.size || 20} height={props.size || 20} fill="currentColor" aria-hidden="true">
      <path d="M18.146 2H21l-6.77 7.73L22 22h-6.828l-4.78-6.217L4.8 22H2l7.3-8.336L2 2h6.914l4.416 5.84L18.146 2zm-2.39 18h1.56L8.42 4H6.75l9.006 16z" />
    </svg>
  );
  const iconMap = { twitter: XIcon, youtube: Youtube, instagram: Instagram, facebook: Facebook };
  const socialLinks = SOCIAL_LINKS.map((s) => ({ ...s, icon: iconMap[s.key] || XIcon }));

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    try {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        setCanInstall(false);
        return;
      }
      // Fallback: show instructions (iOS Safari and unsupported cases)
      setShowInstallHelp(true);
    } catch {}
  };

  const handleShareClick = async () => {
    const shareData = { title: '40 Bags', text: 'Plan epic gameday trips with 40 Bags', url: window.location.origin };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {}
    try {
      await navigator.clipboard.writeText(shareData.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent mb-4">
              40 Bags Entertainment Inc.
            </h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Your ultimate destination for college sports, event tickets, travel booking, and tailgating guides. 
              20+ years of connecting family & friends!
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-300">
                <Mail size={16} />
                <a 
                  href="mailto:info.40bags@gmail.com"
                  className="hover:text-orange-400 transition-colors duration-200"
                >
                  info.40bags@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* Start Planning */}
          <div className="lg:col-span-1">
            <h4 className="text-lg font-semibold text-orange-400 mb-4 uppercase tracking-wide">
              Start Planning
            </h4>
            <div className="space-y-2">
              {startPlanning.map((item, index) => (
                <a
                  key={index}
                  href={item.url}
                  className="block text-gray-300 hover:text-white transition-colors"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>

          {/* Our Partners - Now with proper affiliate links */}
          <div className="lg:col-span-2">
            <h4 className="text-lg font-semibold text-orange-400 mb-4 uppercase tracking-wide">
              Our Partners
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              {partners.map((partner, index) => (
                <a
                  key={index}
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {partner.name}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Social Media Icons */}
        <div className="mt-12 flex justify-center space-x-6">
          {socialLinks.map((social, index) => {
            const IconComponent = social.icon;
            return (
              <a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center text-gray-300 hover:text-white transition-colors"
              >
                <IconComponent size={20} />
              </a>
            );
          })}
        </div>

        {/* Mobile App CTA */}
        <div className="mt-12 p-6 bg-gradient-to-r from-primary-600 to-orange-600 rounded-2xl text-center">
          <h4 className="text-xl font-semibold mb-2">
            Add our site to your mobile phone home screen for easy access
          </h4>
          <button 
            className="btn-secondary bg-white text-primary-700 hover:bg-gray-50 mt-4 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleInstallClick}
            aria-disabled={false}
            title={canInstall ? 'Install app' : 'Show install instructions'}
          >
            {canInstall ? 'Install App' : 'How to Add to Home Screen'}
          </button>
          {showInstallHelp && (
            <div className="mt-4 text-sm text-white/90">
              <p>On iPhone/iPad: tap the share icon in Safari, then "Add to Home Screen".</p>
              <p>On Android: open browser menu and tap "Install app" or "Add to Home screen".</p>
              <button className="mt-2 px-3 py-1 bg-white/20 rounded" onClick={() => setShowInstallHelp(false)}>Got it</button>
            </div>
          )}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-300 text-sm">
              <p>Powered By 40 Bags Entertainment Inc.</p>
            </div>
            <div className="flex space-x-6 text-sm">
              <a href="/privacy" className="text-gray-300 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-gray-300 hover:text-white transition-colors">
                Terms & Conditions
              </a>
              <a href="/disclosure" className="text-gray-300 hover:text-white transition-colors">
                Booking Disclosure
              </a>
            </div>
          </div>
        </div>

        {/* Share Experience */}
        <div className="mt-8 text-center">
          <button onClick={handleShareClick} className="inline-flex items-center gap-2 btn-primary bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 px-4 py-2 rounded-lg">
            <Share2 size={18} />
            <span>{copied ? 'Link Copied!' : 'Share Your Experience'}</span>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;